import type { TableMetric } from "@/features/analytics/constants";
import type { RangeResult } from "schemas/generated/analyticsRead";

import * as React from "react";

import { TableLoadingRows } from "@editor/components/analytics/TableLoadingRows";
import { TablePageCellContent } from "@editor/components/analytics/TablePageCellContent";
import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { generateDeepDiveNavigationPathFromUrl } from "@editor/utils/analytics";

import { formatValueToTableCell } from "@/editor/utils/analytics";
import useWorkspaceUrlHosts from "@/features/analytics/useWorkspaceUrlHosts";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@replo/design-system/components/shadcn/core/table";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { useNavigate } from "react-router-dom";

const TOP_PAGES_TABLE_METRICS: {
  key: TableMetric;
  label: string;
  tooltipContent: string;
}[] = [
  {
    key: "unique_sessions",
    label: "Unique Sessions",
    tooltipContent: "Unique Sessions Definition",
  },
  {
    key: "conversion_rates",
    label: "Conversion Rate",
    tooltipContent: "Conversion Rate Definition",
  },
];

// NOTE (Max, 2025-04-24): Hardcoding for now until we include filters in the homepage.
const START_DATETIME = 1_738_368_000_000;
const END_DATETIME = 1_740_873_600_000;

const PAGE_NAME_COLUMN_WIDTH_CLASSNAME = "w-[280px]";

type TableBodyContentProps = {
  isLoading: boolean;
  topPages: RangeResult[];
};

const TableBodyContent = ({ isLoading, topPages }: TableBodyContentProps) => {
  const workspaceId = useCurrentWorkspaceId();
  const analytics = useLogAnalytics();
  const navigate = useNavigate();
  const workspaceUrlHosts = useWorkspaceUrlHosts(workspaceId ?? null);

  const handleUrlPathClick = () => {
    analytics("analytics.url.open", {
      source: "analytics_insights",
    });
  };

  if (isLoading || !workspaceId) {
    return (
      <TableLoadingRows rows={3} columns={TOP_PAGES_TABLE_METRICS.length + 1} />
    );
  }

  const handlePageTitleClick = ({
    urlPath,
    url,
  }: {
    urlPath: string;
    url: string;
  }) => {
    analytics("analytics.record.click", {
      from: "analytics_insights",
    });

    const { deepDivePathWithCompressedUrlSyncedQuery } =
      generateDeepDiveNavigationPathFromUrl({
        url,
        urlHosts: workspaceUrlHosts.data,
        path: urlPath,
        workspaceId,
        selectedTimePeriod: {
          type: "custom",
          value: {
            from: START_DATETIME,
            to: END_DATETIME,
          },
        },
      });

    navigate(deepDivePathWithCompressedUrlSyncedQuery ?? "");
  };

  return (
    <TableBody>
      {topPages.map((result) => {
        const urlPath = result.urlPath;

        const { title, url } = result.metadata;

        return (
          <TableRow key={urlPath}>
            <TableCell>
              <TablePageCellContent
                title={title}
                url={url}
                urlPath={urlPath}
                onPageTitleClick={() =>
                  handlePageTitleClick({
                    urlPath,
                    url,
                  })
                }
                onUrlPathClick={handleUrlPathClick}
                className={twMerge(PAGE_NAME_COLUMN_WIDTH_CLASSNAME)}
              />
            </TableCell>
            {TOP_PAGES_TABLE_METRICS.map((metric) => {
              const value = result.metrics[metric.key]?.[0];

              return (
                <TableCell key={metric.key} className="text-sm">
                  {value
                    ? formatValueToTableCell({
                        value,
                        name: metric.key,
                        currency: "USD",
                      })
                    : "-"}
                </TableCell>
              );
            })}
          </TableRow>
        );
      })}
    </TableBody>
  );
};

type TopPagesTableProps = {
  isLoading: boolean;
  topPages: RangeResult[];
};

export const TopPagesTable = ({ isLoading, topPages }: TopPagesTableProps) => {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className={PAGE_NAME_COLUMN_WIDTH_CLASSNAME}>
            Page Name
          </TableHead>
          {TOP_PAGES_TABLE_METRICS.map((metric) => (
            <TableHead key={metric.key}>
              <Tooltip triggerAsChild content={metric.tooltipContent}>
                <span>{metric.label}</span>
              </Tooltip>
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBodyContent isLoading={isLoading} topPages={topPages} />
    </Table>
  );
};
